import baselineNewsletterTemplateJson from "../baselineNewsletterTemplate.json";

// BeeFree distinguishes a full page *design* (`{ page: { rows: [...] } }`, what
// the editor loads/saves) from a bare *row* (what a blueprint's content_row,
// link_dump_row, and custom content templates store).
//
// A stored row artifact is either:
//   - a single BeeFree row object, or
//   - `{ rows: [row, row, ...] }` when multiple rows were captured.
//
// The editor can only open a full design, so we wrap rows into a page shell to
// edit them and unwrap the page's rows back out on save. This mirrors the
// backend's storage convention in SkeletonService (length === 1 → single row,
// otherwise `{ rows: [...] }`).

type AnyObj = Record<string, any>;

/** True when the value is a BeeFree full page design. */
export function isFullDesign(value: unknown): value is { page: { rows: any[] } } {
  return (
    !!value &&
    typeof value === "object" &&
    !!(value as AnyObj).page &&
    Array.isArray((value as AnyObj).page.rows)
  );
}

/** Normalize a stored row artifact into an array of BeeFree rows. */
export function extractRows(stored: unknown): any[] {
  if (!stored || typeof stored !== "object") return [];
  const obj = stored as AnyObj;
  if (Array.isArray(obj.rows)) return obj.rows;
  if (Array.isArray(stored)) return stored as any[];
  // A bare single row object.
  return [obj];
}

/**
 * Collapse an array of rows back into the stored artifact shape:
 * a single row object when there's exactly one, else `{ rows: [...] }`.
 */
export function collapseRows(rows: any[]): object {
  if (rows.length === 1) return rows[0];
  return { rows };
}

const deepClone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

/**
 * Wrap stored row(s) into a full BeeFree page design so they can be opened in
 * the editor. `shell` (e.g. the blueprint's default template) is used for page
 * styling/width when available; otherwise the bundled baseline template is used.
 */
export function wrapRowsInDesign(stored: unknown, shell?: object | null): object {
  const base: AnyObj = shell && isFullDesign(shell) ? shell : baselineNewsletterTemplateJson;
  const design = deepClone(base);
  design.page.rows = extractRows(stored);
  return design;
}

/** Extract the stored row artifact from a full design saved by the editor. */
export function unwrapRowsFromDesign(design: unknown): object {
  if (!isFullDesign(design)) return {};
  return collapseRows((design as AnyObj).page.rows);
}
